/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file sets up the logic for retrieving an individual patient.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Used to create logic based side effects for the redux store.
 */
import { createLogic } from 'redux-logic';

/**
 * Typings
 */
import { TypedUseSelectorHook, useSelector } from 'react-redux';

/**
 * Utility library for reducing redux boilerplate while remaining typed. 
 */
import { ImmerReducer, createActionCreators, createReducerFunction } from 'immer-reducer';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

 /*
  * Used to register the logics so that they are included in the store. 
  */
import reducerRegistry from '../ReducerRegistry';

/*
 * Used to get access to the API types and requests
 */
import * as Dtos from '../../api/dtos';

/**
 * Enumeration used to determine the state of a request.
 */
import { IRequestState, RequestState } from '.';

/**
 * Used to create a get request to the servicestack API.
 */
import { createGetRequest } from 'src/utils/createRequest';

/*
* ---------------------------------------------------------------------------------
* Interfaces / Types
* ---------------------------------------------------------------------------------
*/

export interface IIndividualSearchState {
    version: Dtos.Version | null;
    category: Dtos.Category | null;
    term: Dtos.Term | null;
    subterm: Dtos.Term | null;
    grade: Dtos.Grade | null;
}

interface IInitialSearchState extends Record<number, IIndividualSearchState> {
    data: Record<number, IIndividualSearchState>,
    view: number[];
    totalCount: number | null;
    loadState: IRequestState;
}

export interface ISearchState {
   search: IInitialSearchState;
}

/*
 * ---------------------------------------------------------------------------------
 * Initial State
 * ---------------------------------------------------------------------------------
 */

const SearchInitialIndividualState: IIndividualSearchState = {
    version: null,
    category: null,
    term: null,
    subterm: null,
    grade: null
};

const SearchInitialState: IInitialSearchState = {
    data: {},
    view: [],
    totalCount: null,
    loadState: {
        state: RequestState.None
    }
};

/*
* ---------------------------------------------------------------------------------
* Selectors
* ---------------------------------------------------------------------------------
*/

export const useSearchSelector: TypedUseSelectorHook<ISearchState> = useSelector;

export const SearchSelectors = {
    selectSearchResult: (state: ISearchState, index: number) => state.search.data[index] ? state.search.data[index] : SearchInitialIndividualState,
    selectData: (state: ISearchState) => state.search.data,
    selectView: (state: ISearchState) => state.search.view,
    selectTotalCount: (state: ISearchState) => state.search.totalCount,
    selectLoadState: (state: ISearchState) => state.search.loadState
};

/*
* ---------------------------------------------------------------------------------
* Reducer
* ---------------------------------------------------------------------------------
*/

class SearchReducer extends ImmerReducer<IInitialSearchState> {
    public search(versionId: number, query: string | undefined, limit: number | undefined, set: number | undefined) {
        this.draftState.loadState = {
            state: RequestState.Pending
        };
    }

    public searchSuccess(results: Dtos.SearchResult[], totalCount: number) {
        const currentCount = Object.keys(this.draftState.data).length;

        results.forEach((result, index) => {
            const id = currentCount + index;

            this.draftState.data[id] = {...SearchInitialIndividualState, ...result};

            this.draftState.view.push(id);
        })



        this.draftState.totalCount = totalCount;
        this.draftState.loadState = {
            state: RequestState.Success
        };
    }

    public searchFailure(responseStatus: Dtos.ResponseStatus) {
        this.draftState.loadState = {
            state: RequestState.Failure,
            responseStatus
        };
    }

    public clear() {
        this.draftState.data = SearchInitialState.data;
        this.draftState.loadState = SearchInitialState.loadState;
        this.draftState.totalCount = SearchInitialState.totalCount;
        this.draftState.view = SearchInitialState.view;
    }
}

export const SearchActions = createActionCreators(SearchReducer);
const reducer = createReducerFunction(SearchReducer, SearchInitialState);
/*
 * ---------------------------------------------------------------------------------
 * API
 * ---------------------------------------------------------------------------------
 */

export const SearchApi = {
    loadById: createGetRequest(
        Dtos.Search,
        (versionId: number, query?: string, limit?: number, set?: number) => ({
            versionId,
            query,
            limit,
            set
        })
    )
}

/*
 * ---------------------------------------------------------------------------------
 * Logic
 * ---------------------------------------------------------------------------------
 */

const SearchLogic = {
    search: createLogic<ISearchState, {}, undefined, string, ReturnType<typeof SearchActions.search>>({
        type: SearchActions.search.type,
        cancelType: SearchActions.clear.type,
        latest: true,
        process: async ({ action }, dispatch, done) => {
            try {
                const [ versionId, query, limit, set ] = action.payload

                const response = await SearchApi.loadById(versionId, query, limit, set);
                dispatch(SearchActions.searchSuccess(
                    response.results,
                    response.totalCount
                ));
            }
            catch (error) {
                dispatch(SearchActions.searchFailure(error ? error.responseStatus : undefined));
            }

            done();
        }
    })
};

const logics = [
    SearchLogic.search
]

/*
 * ---------------------------------------------------------------------------------
 * Register
 * ---------------------------------------------------------------------------------
 */

reducerRegistry.register('search', reducer, logics);


// WEBPACK FOOTER //
// ./src/store/reducers/search.ts